import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import axios from "axios";

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within AppProvider");
  return context;
};

const API_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000/api" ||
  "https://juice-backend-9uhd.onrender.com/api";

if (!process.env.REACT_APP_API_URL) {
  console.warn(
    "⚠️  REACT_APP_API_URL is not set. API calls will fail in production.",
  );
}

// Axios instance
export const api = axios.create({ baseURL: API_URL });

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("Protein Spot_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(
    localStorage.getItem("Protein Spot_token"),
  );
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  // Ref so the 401 interceptor can call logout without stale closure
  const logoutRef = useRef(null);

  // ── Global 401 interceptor ─────────────────────────
  // If any API call returns 401, the token is expired/invalid → log out
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && logoutRef.current) {
          logoutRef.current();
        }
        return Promise.reject(err);
      },
    );
    return () => api.interceptors.response.eject(interceptor);
  }, []);

  // ── Bootstrap on mount ─────────────────────────────
  useEffect(() => {
    const savedToken = localStorage.getItem("Protein Spot_token");
    const savedCart = localStorage.getItem("Protein Spot_cart");

    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch {}
    }

    if (savedToken) {
      // Fetch a fresh user from the API — don't trust localStorage alone
      api
        .get("/users/profile")
        .then((res) => {
          const freshUser = res.data;
          setUser(freshUser);
          setToken(savedToken);
          // Keep localStorage in sync with fresh data
          localStorage.setItem("Protein Spot_user", JSON.stringify(freshUser));
        })
        .catch(() => {
          // Token invalid / expired — clear everything
          localStorage.removeItem("Protein Spot_token");
          localStorage.removeItem("Protein Spot_user");
          setUser(null);
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // ── Persist cart ───────────────────────────────────
  useEffect(() => {
    localStorage.setItem("Protein Spot_cart", JSON.stringify(cart));
  }, [cart]);

  // ── Auth actions ───────────────────────────────────
  const login = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem("Protein Spot_token", userToken);
    localStorage.setItem("Protein Spot_user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("Protein Spot_token");
    localStorage.removeItem("Protein Spot_user");
  };

  // Keep ref in sync so the interceptor always calls current logout
  logoutRef.current = logout;

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("Protein Spot_user", JSON.stringify(userData));
  };

  // ── Cart functions ─────────────────────────────────
  const addToCart = (product, quantity = 1) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.productId === product._id);
      if (existing) {
        return prev.map((item) =>
          item.productId === product._id
            ? { ...item, quantity: item.quantity + quantity }
            : item,
        );
      }
      return [
        ...prev,
        {
          productId: product._id,
          name: product.name,
          price: product.price,
          image: product.image,
          category: product.category,
          quantity,
        },
      ];
    });
  };

  const removeFromCart = (productId) =>
    setCart((prev) => prev.filter((item) => item.productId !== productId));

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.productId === productId ? { ...item, quantity } : item,
      ),
    );
  };

  const clearCart = () => setCart([]);

  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const deliveryFee = cartTotal >= 299 ? 0 : 40;

  return (
    <AppContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        updateUser,
        loading,
        cart,
        addToCart,
        removeFromCart,
        updateCartQuantity,
        clearCart,
        cartTotal,
        cartCount,
        deliveryFee,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};




